import { useState, useEffect, useCallback } from "react";
import { Box, Container, Text, Badge, ActionIcon, Group, Paper, Loader, Center, Button, Stack } from "@mantine/core";
import { IconEye, IconDownload, IconWallet } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";

interface InvoiceData {
  id: string;
  companyReferenceId: string;
  date: string;
  paymentMethod: "GT_WALLET" | "ONLINE";
  amount: number;
  currency: string;
  status: string;
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [loading, setLoading] = useState(true);

  const loadInvoices = useCallback(async () => {
    try {
      if (hasProfile === null) return;
      setLoading(true);
      // Fetch ledger entries for the company
      if (userDetails?.company?.id) {
        const ledgerData = await trpc.wallet.getTransactions.query({ filter: "INVOICE" });

        // Transform ledger data to invoice format
        const formattedInvoices: InvoiceData[] = ledgerData.map((entry: any) => ({
          id: entry.id,
          companyReferenceId: entry.matchedCompanyGtid || "N/A",
          date: new Date(entry.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          }),
          paymentMethod: entry.reason === "TOPUP" ? "ONLINE" : "GT_WALLET",
          amount: entry.amount,
          currency: entry.currency,
          status: "COMPLETED",
        }));

        setInvoices(formattedInvoices);
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleViewInvoice = () => {
    // Navigate to invoice detail page
    // console.log("View invoice:", invoiceId);
  };

  const handleDownloadInvoice = () => {
    // Download invoice PDF
    // console.log("Download invoice:", invoiceId);
  };

  const handleViewWallet = () => {
    navigate("/invoices/wallet");
  };

  if (loading) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent
          title="Invoices & Payments"
          description="Complete record of all financial transactions and invoices"
        />
        <Container size="xl" mt="xl">
          <Center h={300}>
            <Loader size="lg" />
          </Center>
        </Container>
      </Box>
    );
  }

  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent
        title="Invoices & Payments"
        description="Complete record of all financial transactions and invoices"
        rightSection={
          <Button leftSection={<IconWallet size={18} />} variant="filled" color="primary" onClick={handleViewWallet}>
            View Wallet
          </Button>
        }
      />

      <Container size="xl" mt="xl">
        {invoices.length === 0 ? (
          <Paper p="xl" radius="lg" withBorder>
            <Stack align="center" gap="md" py="xl">
              <IconWallet size={48} color="var(--mantine-color-gray-5)" />
              <Text size="lg" fw={500} c="slate.5">
                No invoices yet
              </Text>
              <Text size="sm" c="slate.5" ta="center">
                Your payment history and invoices will appear here once you make transactions.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Stack gap="md">
            {invoices.map((invoice) => (
              <Paper key={invoice.id} p="md" radius="md" withBorder>
                <Group justify="space-between" align="center">
                  <Stack gap={4}>
                    <Text size="sm" fw={600} c="dark.9">
                      {invoice.companyReferenceId}
                    </Text>
                    <Text size="xs" c="slate.7">
                      {invoice.date}
                    </Text>
                  </Stack>

                  <Badge
                    variant="light"
                    color={invoice.paymentMethod === "GT_WALLET" ? "blue" : "green"}
                    radius="sm"
                    size="lg"
                    tt="capitalize"
                    styles={{
                      root: {
                        fontWeight: 500,
                        fontSize: "13px",
                        padding: "6px 12px",
                      },
                    }}
                  >
                    {invoice.paymentMethod === "GT_WALLET" ? "GT Wallet" : "Online"}
                  </Badge>

                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      color="gray.6"
                      size="lg"
                      onClick={() => handleViewInvoice()}
                      title="View invoice"
                    >
                      <IconEye size={20} stroke={1.5} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="gray.6"
                      size="lg"
                      onClick={() => handleDownloadInvoice()}
                      title="Download invoice"
                    >
                      <IconDownload size={20} stroke={1.5} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
